<template>
  <div class="alarm-codes d-flex flex-column min-h-0 flex-grow-1">
    <div class="content d-flex flex-column min-h-0 flex-grow-1">
      <!-- 标题区 + 筛选/操作 -->
      <div class="page-header">
        <div class="header-titles">
          <h2>告警编码</h2>
          <div class="desc">维护设备故障编码、等级及多语言故障描述</div>
        </div>
        <div class="header-actions">
          <v-select v-model="productModel" :items="productModelFilterItems" placeholder="产品型号" variant="outlined"
            density="compact" hide-details rounded="lg" style="max-width: 170px" @update:model-value="onFilterChange" />
          <v-select v-model="type" :items="deviceTypeFilterItems" placeholder="设备类型" variant="outlined"
            density="compact" hide-details rounded="lg" style="max-width: 150px" @update:model-value="onFilterChange" />
          <v-select v-model="moduleType" :items="moduleTypeFilterItems" placeholder="模块类型" variant="outlined"
            density="compact" hide-details rounded="lg" style="max-width: 140px" @update:model-value="onFilterChange" />
          <v-text-field v-model="code" placeholder="搜索故障编码" clearable variant="outlined" density="compact" hide-details
            rounded="lg" style="max-width: 200px" prepend-inner-icon="mdi-magnify" @keyup.enter="onFilterChange"
            @click:clear="onFilterChange" />
          <v-btn color="primary" variant="flat" rounded="lg" class="text-none" @click="onFilterChange">查询</v-btn>
          <v-btn variant="outlined" color="primary" rounded="lg" class="text-none" @click="reset">重置</v-btn>
          <v-btn color="primary" variant="flat" rounded="lg" class="text-none" prepend-icon="mdi-plus" :disabled="!canAddCode"
            @click="createCode">新增编码</v-btn>
        </div>
      </div>

      <!-- 表格 -->
      <v-card class="table-card flex-grow-1 min-h-0 d-flex flex-column" variant="flat" :loading="loading">
        <div class="table-scroll flex-grow-1 min-h-0">
          <v-table fixed-header class="gm-table">
            <thead>
              <tr>
                <th style="width: 160px">告警编码</th>
                <th style="width: 140px">产品型号</th>
                <th style="width: 110px">设备类型</th>
                <th style="width: 100px">模块类型</th>
                <th style="width: 100px">故障编码</th>
                <th style="width: 110px">故障等级</th>
                <th style="width: 100px">客户展示</th>
                <th style="width: 220px">故障描述</th>
                <th style="width: 260px">故障详情</th>
                <th style="width: 130px" class="text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableData" :key="row.id">
                <td><b class="code-cell">{{ row.alarmCode || '--' }}</b></td>
                <td>{{ productModelMap[row.productModel] || '--' }}</td>
                <td>
                  <span class="type-tag" :class="`type-tag--${row.type}`">{{ deviceTypeMap[row.type] || '--' }}</span>
                </td>
                <td>{{ moduleTypeMap[row.moduleType] || '--' }}</td>
                <td>{{ row.code }}</td>
                <td><v-chip :color="getLevelColor(row.level)" variant="tonal" size="small">{{ levelItems.find(item => item.value === row.level)?.title || '--' }}</v-chip></td>
                <td>{{ row.customerShow === 1 ? '是' : '否' }}</td>
                <td>
                  <span class="text-cell" :title="row.description || ''">{{ row.description || '--' }}</span>
                </td>
                <td>
                  <span class="text-cell" :title="row.details || ''">{{ row.details || '--' }}</span>
                </td>
                <td class="text-center">
                  <v-btn variant="text" color="primary" size="small" class="text-none" :disabled="!canEditCode" @click="editCode(row)">编辑</v-btn>
                  <v-btn variant="text" color="error" size="small" class="text-none" :disabled="!canDeleteCode" @click="askDelete(row)">删除</v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>

          <div v-if="!loading && tableData.length === 0" class="empty-state">
            暂无数据
          </div>
        </div>

        <!-- 分页 -->
        <TablePagination v-show="total > 0" :total="total" :pageSize="pageSize" v-model="pageNum" :disabled="loading"
          @change="onPageChange" />
      </v-card>
    </div>

    <!-- 新增/编辑弹窗 -->
    <v-dialog v-model="dialogVisible" max-width="760" persistent scroll-strategy="close">
      <v-card rounded="lg" class="code-dialog">
        <div class="dlg-head">
          <span class="dlg-title">{{ dialogTitle }}</span>
          <v-btn icon="mdi-close" variant="text" size="small" density="comfortable" @click="dialogVisible = false" />
        </div>

        <v-card-text class="dlg-body">
          <div class="form-grid">
            <div class="form-group">
              <label>产品型号 <span class="required-star">*</span></label>
              <v-select v-model="dialogForm.productModel" :items="productModelItems" placeholder="请选择产品型号"
                variant="outlined" density="compact" hide-details />
            </div>

            <div class="form-group">
              <label>设备类型 <span class="required-star">*</span></label>
              <v-select v-model="dialogForm.type" :items="deviceTypeItems" placeholder="请选择设备类型" variant="outlined"
                density="compact" hide-details />
            </div>

            <div class="form-group">
              <label>模块类型 <span class="required-star">*</span></label>
              <v-select v-model="dialogForm.moduleType" :items="moduleTypeItems" placeholder="请选择模块类型"
                variant="outlined" density="compact" hide-details />
            </div>

            <div class="form-group">
              <label>故障编码 <span class="required-star">*</span></label>
              <v-text-field v-model="dialogForm.code" type="number" placeholder="请输入故障编码" variant="outlined"
                density="compact" hide-details />
            </div>

            <div class="form-group">
              <label>故障等级 <span class="required-star">*</span></label>
              <v-select v-model="dialogForm.level" :items="levelItems" placeholder="请选择故障等级" variant="outlined"
                density="compact" hide-details />
            </div>

            <div class="form-group">
              <label>告警唯一编码</label>
              <v-text-field v-model="dialogForm.alarmCode" placeholder="如 A1-01-00-2001" variant="outlined"
                density="compact" hide-details />
            </div>

            <div class="form-group grid-full">
              <label>给客户展示</label>
              <v-radio-group v-model="dialogForm.customerShow" inline hide-details color="primary">
                <v-radio :value="0" label="否" />
                <v-radio :value="1" label="是" />
              </v-radio-group>
            </div>

            <div class="form-group grid-full">
              <div class="field-label-row">
                <label class="mb-0">故障描述</label>
                <v-radio-group v-model="description_type" inline hide-details density="compact" color="primary">
                  <v-radio :value="1" label="中文" />
                  <v-radio :value="2" label="英文" />
                  <v-radio :value="3" label="意大利语" />
                </v-radio-group>
              </div>
              <v-textarea v-if="description_type === 1" v-model="dialogForm.description" placeholder="请输入故障描述（中文）"
                :rows="2" variant="outlined" density="compact" hide-details />
              <v-textarea v-else-if="description_type === 2" v-model="dialogForm.descriptionEn"
                placeholder="请输入故障描述（英文）" :rows="2" variant="outlined" density="compact" hide-details />
              <v-textarea v-else v-model="dialogForm.descriptionIta" placeholder="请输入故障描述（意大利语）" :rows="2"
                variant="outlined" density="compact" hide-details />
            </div>

            <div class="form-group grid-full">
              <div class="field-label-row">
                <label class="mb-0">故障详情</label>
                <v-radio-group v-model="details_type" inline hide-details density="compact" color="primary">
                  <v-radio :value="1" label="中文" />
                  <v-radio :value="2" label="英文" />
                  <v-radio :value="3" label="意大利语" />
                </v-radio-group>
              </div>
              <v-textarea v-if="details_type === 1" v-model="dialogForm.details" placeholder="请输入故障详情（中文）" :rows="2"
                variant="outlined" density="compact" hide-details />
              <v-textarea v-else-if="details_type === 2" v-model="dialogForm.detailsEn" placeholder="请输入故障详情（英文）"
                :rows="2" variant="outlined" density="compact" hide-details />
              <v-textarea v-else v-model="dialogForm.detailsIta" placeholder="请输入故障详情（意大利语）" :rows="2"
                variant="outlined" density="compact" hide-details />
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dlg-actions">
          <v-spacer />
          <v-btn variant="text" rounded="lg" class="text-none" @click="dialogVisible = false">取消</v-btn>
          <v-btn color="primary" variant="flat" rounded="lg" class="text-none" :loading="submitting" :disabled="!canSubmitDialog"
            @click="onDialogConfirm">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 删除确认弹窗 -->
    <v-dialog v-model="deleteVisible" max-width="420" scroll-strategy="close">
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-semibold">删除确认</v-card-title>
        <v-card-text class="text-body-2 pt-2">
          确定删除告警编码「<strong>{{ deleteRow?.alarmCode || deleteRow?.code }}</strong>」吗？此操作不可恢复。
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="deleteVisible = false">取消</v-btn>
          <v-btn color="error" variant="flat" rounded="lg" class="text-none" :loading="deleting" :disabled="!canDeleteCode"
            @click="confirmDelete">删除</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { listAlarmCode, addAlarmCode, updateAlarmCode, delAlarmCode } from '@/api/device/alarmCode'
import { eui } from 'ems-ui'
import TablePagination from '@/components/TablePagination/index.vue'
import { checkPermi } from '@/utils/permission'

// 产品型号 A0/A1/A2
const productModelItems = [
  { title: '运行类告警', value: 'A0' },
  { title: '工商业设备告警', value: 'A1' },
  { title: 'eBlock100C告警', value: 'A2' },
]
const productModelMap = Object.fromEntries(productModelItems.map(i => [i.value, i.title]))

// 设备类型
const deviceTypeItems = [
  { title: '默认', value: '00' },
  { title: 'PCS', value: '01' },
  { title: 'BCS', value: '02' },
  { title: 'BMS', value: '03' },
  { title: 'eLink', value: '04' },
  { title: '水机', value: '05' },
  { title: '消防', value: '06' },
  { title: '100C-dcdc', value: '07' },
  { title: '100C-MPPT', value: '08' },
  { title: 'eBlock', value: 'E1' },
  { title: 'EMS', value: 'E2' },
]
const deviceTypeMap = Object.fromEntries(deviceTypeItems.map(i => [i.value, i.title]))

// 模块类型：00 默认，其余为运营类告警
const moduleTypeItems = [
  { title: '默认', value: '00' },
  { title: '需量', value: '01' },
  { title: '逆流', value: '02' },
  { title: '非计划', value: '03' },
  { title: '离线', value: '04' },
  { title: '电压', value: '05' },
  { title: '温度', value: '06' },
  { title: '收益', value: '07' },
  { title: '效率', value: '08' },
  { title: 'soc', value: '09' },
]
const moduleTypeMap = Object.fromEntries(moduleTypeItems.map(i => [i.value, i.title]))

const levelItems = [
  { title: '提示', value: 0 },
  { title: '告警', value: 1 },
  { title: '停机故障', value: 2 },
  { title: '严重故障', value: 3 },
]
const getLevelColor = (level) => {
  switch (level) {
    case 0: return 'primary'
    case 1: return 'warning'
    case 2: return 'error'
    case 3: return 'error'
    default: return 'default'
  }
}

// 筛选项在各自列表前加「全部」
const withAll = (items) => [{ title: '全部', value: '' }, ...items]
const productModelFilterItems = withAll(productModelItems)
const deviceTypeFilterItems = withAll(deviceTypeItems)
const moduleTypeFilterItems = withAll(moduleTypeItems)

const pageNum = ref(1)
const pageSize = ref(10)
const total = ref(0)
const tableData = ref([])

const code = ref('')
const type = ref('')
const productModel = ref('')
const moduleType = ref('')

const loading = ref(false)
const canAddCode = computed(() => checkPermi(['ops:alarmCode:add']))
const canEditCode = computed(() => checkPermi(['ops:alarmCode:edit']))
const canDeleteCode = computed(() => checkPermi(['ops:alarmCode:delete']))

async function getCodeList() {
  // 空字符串会被后端当作精确匹配条件，需剔除
  const params = {
    pageNum: pageNum.value,
    pageSize: pageSize.value,
  }
  if (code.value !== '' && code.value !== null) params.code = Number(code.value)
  if (type.value) params.type = type.value
  if (productModel.value) params.productModel = productModel.value
  if (moduleType.value) params.moduleType = moduleType.value

  try {
    loading.value = true
    const res = await listAlarmCode(params)
    tableData.value = res.rows || []
    total.value = res.total || 0
  } catch (error) {
    console.warn(error)
  } finally {
    loading.value = false
  }
}

// 筛选变化：回到第一页并查询
function onFilterChange() {
  pageNum.value = 1
  getCodeList()
}

function onPageChange(page) {
  pageNum.value = page
  getCodeList()
}

function reset() {
  pageNum.value = 1
  pageSize.value = 10
  code.value = ''
  type.value = ''
  productModel.value = ''
  moduleType.value = ''
  getCodeList()
}

getCodeList()

// ===== 新增/编辑弹窗 =====
const dialogRole = ref('add')
const dialogVisible = ref(false)
const submitting = ref(false)
const dialogTitle = computed(() => (dialogRole.value === 'add' ? '新增' : '编辑') + ' 故障编码')
const canSubmitDialog = computed(() => (dialogRole.value === 'add' ? canAddCode.value : canEditCode.value))

const defaultForm = () => ({
  id: null,
  productModel: '',
  type: '',
  moduleType: '00',
  code: '',
  alarmCode: '',
  level: '',
  customerShow: 0,
  description: '',
  descriptionEn: '',
  descriptionIta: '',
  details: '',
  detailsEn: '',
  detailsIta: '',
})

const dialogForm = reactive(defaultForm())

// 弹窗内描述/详情的语言切换（1 中文 / 2 英文 / 3 意大利语）
const description_type = ref(1)
const details_type = ref(1)

function createCode() {
  if (!canAddCode.value) return
  dialogRole.value = 'add'
  Object.assign(dialogForm, defaultForm())
  description_type.value = 1
  details_type.value = 1
  dialogVisible.value = true
}

function editCode(row) {
  if (!canEditCode.value) return
  dialogRole.value = 'edit'
  // 以 defaultForm 打底，保证 row 里缺失的字段不会残留上一次编辑的值
  Object.assign(dialogForm, defaultForm(), row)
  description_type.value = 1
  details_type.value = 1
  dialogVisible.value = true
}

async function onDialogConfirm() {
  if (!canSubmitDialog.value) return
  // 手动校验（项目约定不用 element rules）
  if (!dialogForm.productModel) {
    eui.showToast('请选择产品型号', 'error')
    return
  }
  if (!dialogForm.type) {
    eui.showToast('请选择设备类型', 'error')
    return
  }
  if (!dialogForm.moduleType) {
    eui.showToast('请选择模块类型', 'error')
    return
  }
  if (dialogForm.code === '' || dialogForm.code === null || isNaN(Number(dialogForm.code))) {
    eui.showToast('请输入数字格式的故障编码', 'error')
    return
  }
  // 等级 0 是合法值，不能用真值判断
  if (dialogForm.level === '' || dialogForm.level === null) {
    eui.showToast('请选择故障等级', 'error')
    return
  }

  // 修改接口要求提交全量对象：productModel/type/moduleType/code/level
  // 即使未改动也必须携带原值，否则后端参数校验失败
  const postData = {
    ...dialogForm,
    code: Number(dialogForm.code),
    level: Number(dialogForm.level),
  }
  if (dialogRole.value === 'add') delete postData.id

  try {
    submitting.value = true
    const res = dialogRole.value === 'add'
      ? await addAlarmCode(postData)
      : await updateAlarmCode(postData)

    // 四字段联合唯一冲突等业务错误由后端以 code=500 返回，不会走 catch
    if (res && res.code !== 200) {
      eui.showToast(res.msg || '操作失败', 'error')
      return
    }
    eui.showToast(dialogRole.value === 'add' ? '新增成功' : '更新成功', 'success')
    dialogVisible.value = false
    await getCodeList()
  } catch (error) {
    console.warn(error)
  } finally {
    submitting.value = false
  }
}

// ===== 删除确认 =====
const deleteVisible = ref(false)
const deleting = ref(false)
const deleteRow = ref(null)

function askDelete(row) {
  if (!canDeleteCode.value) return
  deleteRow.value = row
  deleteVisible.value = true
}

async function confirmDelete() {
  if (!canDeleteCode.value) return
  try {
    deleting.value = true
    const res = await delAlarmCode(deleteRow.value.id)
    if (res && res.code !== 200) {
      eui.showToast(res.msg || '删除失败', 'error')
      return
    }
    eui.showToast('删除成功', 'success')
    deleteVisible.value = false
    // 删掉当前页最后一条时回退一页，避免停在空页
    if (tableData.value.length === 1 && pageNum.value > 1) pageNum.value -= 1
    await getCodeList()
  } catch (error) {
    console.warn(error)
  } finally {
    deleting.value = false
  }
}
</script>

<style scoped lang="scss">
/* 与 alarm 模块 / 全局 variables.scss 对齐的设计 token */
.alarm-codes {
  --primary: var(--color-primary);
  --card-bg: rgba(255, 255, 255, 0.82);
  --border: #e9efff;
  --surface-muted: #eef1fb;
  --text-primary: var(--color-text);
  --text-secondary: color-mix(in srgb, var(--color-text) 78%, #7a8199);
  --text-muted: color-mix(in srgb, var(--color-text) 55%, #a0a6bd);
  --shadow-card: 0 2px 8px rgba(8, 40, 87, 0.05);

  min-width: 0;
  /* 项目无 min-h-0 工具类，需用真实 CSS 让 flex 链可收缩 */
  min-height: 0;
  color: var(--text-primary);
}

.content {
  padding: 16px 20px 20px;
  box-sizing: border-box;
  min-height: 0;
  /* 内容自身裁剪，避免外层 alarm-tab-panel 的 overflow-auto 连同 page-header 一起滚动 */
  overflow: hidden;
}

/* 标题区（固定，不随表格滚动） */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-shrink: 0;
  flex-wrap: wrap;

  .header-titles {
    flex-shrink: 0;
  }

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }

  .desc {
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 4px;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex: 1;
  min-width: 0;
}

/* 表格卡片 */
.table-card {
  background: var(--card-bg);
  border: 1px solid #fff;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(6px);
  overflow: hidden;
  min-height: 0;
}

.table-scroll {
  overflow: auto;
  min-height: 0;
}

.gm-table {
  width: 100%;
  background: transparent;

  /* 由 .table-scroll 统一负责滚动，关闭 v-table 自身 wrapper 的滚动，
     使 sticky 表头/操作列相对 .table-scroll 定位 */
  :deep(.v-table__wrapper) {
    overflow: visible;
  }

  :deep(thead th) {
    font-weight: 600 !important;
    font-size: 13px;
    color: var(--text-secondary) !important;
    background: var(--surface-muted) !important;
    border-bottom: 1px solid var(--color-th-border) !important;
    height: 48px !important;
    white-space: nowrap;
    /* 纵向滚动时固定表头 */
    position: sticky;
    top: 0;
    z-index: 2;
  }

  :deep(tbody td) {
    font-size: 13px;
    color: var(--text-primary);
    border-bottom: 1px solid var(--color-td-border) !important;
    height: 50px !important;
  }

  /* 列宽以 th 上声明的为准，不被单元格内容撑开；
     窄屏时保底 1430px（各列声明宽度之和），由 .table-scroll 横向滚动接管 */
  table {
    table-layout: fixed;
    width: 100%;
    min-width: 1430px;
  }

  /* 固定宽度的短列不换行，超出用省略号 */
  :deep(tbody td),
  :deep(thead th) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(tbody tr) {
    transition: background 0.15s ease;
  }

  :deep(tbody tr:hover) {
    background: color-mix(in srgb, var(--color-primary) 4%, #fff);
  }

  :deep(tbody tr:last-child td) {
    border-bottom: none !important;
  }

  /* 固定右侧操作列 */
  :deep(thead th:last-child),
  :deep(tbody td:last-child) {
    position: sticky;
    right: 0;
    z-index: 1;
    white-space: nowrap;
    box-shadow: -6px 0 8px -6px rgba(8, 40, 87, 0.12);
  }

  :deep(thead th:last-child) {
    z-index: 3;
    background: var(--surface-muted) !important;
  }

  :deep(tbody td:last-child) {
    background: #fff;
  }

  :deep(tbody tr:hover td:last-child) {
    background: color-mix(in srgb, var(--color-primary) 4%, #fff);
  }
}

.code-cell {
  color: var(--primary);
  font-weight: 600;
}

/* 描述/详情：单行省略，完整内容通过 title 悬浮查看 */
.text-cell {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-tag {
  display: inline-block;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 10px;
  /* 默认（未知类型）配色 */
  background: color-mix(in srgb, var(--color-primary) 10%, #ffffff);
  color: var(--primary);
}

/* 不同设备类型不同配色，与 deviceTypeItems 的取值对应 */
.type-tag--01 {
  background: color-mix(in srgb, #16a34a 12%, #ffffff);
  color: #16a34a;
}

.type-tag--02 {
  background: color-mix(in srgb, #2f7bff 12%, #ffffff);
  color: #2f7bff;
}

.type-tag--03 {
  background: color-mix(in srgb, #0d9488 12%, #ffffff);
  color: #0d9488;
}

.type-tag--04 {
  background: color-mix(in srgb, #7c3aed 12%, #ffffff);
  color: #7c3aed;
}

.type-tag--05 {
  background: color-mix(in srgb, #0891b2 12%, #ffffff);
  color: #0891b2;
}

.type-tag--06 {
  background: color-mix(in srgb, #ea580c 12%, #ffffff);
  color: #ea580c;
}

.type-tag--07 {
  background: color-mix(in srgb, #c026d3 12%, #ffffff);
  color: #c026d3;
}

.type-tag--08 {
  background: color-mix(in srgb, #ca8a04 12%, #ffffff);
  color: #ca8a04;
}

.type-tag--E1 {
  background: color-mix(in srgb, #9333ea 12%, #ffffff);
  color: #9333ea;
}

.type-tag--E2 {
  background: color-mix(in srgb, #db2777 12%, #ffffff);
  color: #db2777;
}

.empty-state {
  text-align: center;
  color: var(--text-muted);
  padding: 48px 0;
  font-size: 13px;
}

/* 弹窗 */
.code-dialog {
  .dlg-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 12px;
    border-bottom: 1px solid var(--color-td-border);
  }

  .dlg-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }

  :deep(.dlg-body) {
    padding: 18px 20px 8px;
    max-height: 64vh;
    overflow-y: auto;
  }

  .dlg-actions {
    padding: 8px 20px 18px;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  .form-group {
    label {
      display: block;
      margin-bottom: 6px;
      font-size: 13px;
      font-weight: 500;
      color: var(--text-secondary);
    }

    &.grid-full {
      grid-column: 1 / -1;
    }
  }
}

.field-label-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.required-star {
  color: var(--color-error);
  margin-left: 2px;
}
</style>
